import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Collections as CollectionsIcon,
  Search as SearchIcon,
  CurrencyExchange as CurrencyIcon,
  CloudSync as CloudIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
  ViewList as ViewListIcon,
  Language as LanguageIcon,
  Book as BookIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';

function LandingPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState(null);

  // Geolocation-based language detection
  React.useEffect(() => {
    const detectLanguageFromLocation = async () => {
      try {
        // First try IP-based geolocation (more reliable, no permission needed)
        const ipResponse = await fetch('https://api.bigdatacloud.net/data/ip-geolocation-full?key=free');
        if (ipResponse.ok) {
          const ipData = await ipResponse.json();
          const countryCode = ipData.country?.isoAlpha2;
          
          if (countryCode) {
            const countryLanguageMap = {
              'TH': 'th', // Thailand -> Thai
              'CN': 'zh', // China -> Chinese
              'JP': 'ja', // Japan -> Japanese
              // Add more countries as needed
            };

            const detectedLanguage = countryLanguageMap[countryCode];
            
            if (detectedLanguage && detectedLanguage !== currentLanguage) {
              console.log(`IP detected country: ${ipData.country?.name} (${countryCode}), setting language to: ${detectedLanguage}`);
              changeLanguage(detectedLanguage);
              return;
            } else if (detectedLanguage) {
              console.log(`IP detected country: ${ipData.country?.name} (${countryCode}), language already set to: ${detectedLanguage}`);
              return;
            } else {
              console.log(`IP detected country: ${ipData.country?.name} (${countryCode}), using default language: en`);
              return;
            }
          }
        }

        // Fallback to browser geolocation if IP method fails
        if (navigator.geolocation) {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 300000 // 5 minutes cache
            });
          });

          const { latitude, longitude } = position.coords;

          // Use reverse geocoding to get country
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          
          if (response.ok) {
            const data = await response.json();
            const countryCode = data.countryCode;

            const countryLanguageMap = {
              'TH': 'th', // Thailand -> Thai
              'CN': 'zh', // China -> Chinese
              'JP': 'ja', // Japan -> Japanese
            };

            const detectedLanguage = countryLanguageMap[countryCode];
            
            if (detectedLanguage && detectedLanguage !== currentLanguage) {
              console.log(`GPS detected country: ${data.countryName} (${countryCode}), setting language to: ${detectedLanguage}`);
              changeLanguage(detectedLanguage);
            } else if (detectedLanguage) {
              console.log(`GPS detected country: ${data.countryName} (${countryCode}), language already set to: ${detectedLanguage}`);
            } else {
              console.log(`GPS detected country: ${data.countryName} (${countryCode}), using default language: en`);
            }
          }
        }
      } catch (error) {
        console.log('Error detecting location or setting language:', error.message);
        // Fallback to default language (English)
      }
    };

    // Only run geolocation detection if no language is set in localStorage
    const savedLanguage = localStorage.getItem('bookport_language');
    if (!savedLanguage) {
      detectLanguageFromLocation();
    }
  }, [currentLanguage, changeLanguage]);

  const handleLanguageClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (lang) => {
    changeLanguage(lang);
    handleLanguageClose();
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'th', name: 'ไทย', flag: '🇹🇭' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ];

  const exampleBooks = [
    {
      title: 'The Great Gatsby',
      author: 'F. Scott Fitzgerald',
      publisher: 'Scribner',
      genre: 'วรรณกรรม',
      price: 350,
      imageUrl: 'https://placehold.co/300x200/e0e0e0/666666?text=The+Great+Gatsby'
    },
    {
      title: 'Harry Potter and the Sorcerer\'s Stone',
      author: 'J.K. Rowling',
      publisher: 'Scholastic',
      genre: 'หนังสือเด็ก',
      price: 450,
      imageUrl: 'https://placehold.co/300x200/e0e0e0/666666?text=Harry+Potter'
    },
    {
      title: 'The Art of War',
      author: 'Sun Tzu',
      publisher: 'นานมีบุ๊คส์',
      genre: 'หนังสือวิชาการ',
      price: 280,
      imageUrl: 'https://placehold.co/300x200/e0e0e0/666666?text=Art+of+War'
    }
  ];

  const features = [
    {
      icon: <CollectionsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.completeManagement.title') || 'Complete Book Management',
      description: t('landing.features.completeManagement.description') || 'Organize your entire book collection with detailed information including author, publisher, genre, and more.'
    },
    {
      icon: <SearchIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.smartSearch.title') || 'Smart Search',
      description: t('landing.features.smartSearch.description') || 'Find any book in your collection instantly with powerful search by title, author, ISBN, or publisher.'
    },
    {
      icon: <CurrencyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.multiCurrency.title') || 'Multi-Currency Support',
      description: t('landing.features.multiCurrency.description') || 'Track book values in your preferred currency with automatic exchange rate updates.'
    },
    {
      icon: <BookIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.bookTracking.title') || 'Book Tracking',
      description: t('landing.features.bookTracking.description') || 'Keep track of your reading progress, book conditions, and collection growth over time.'
    },
    {
      icon: <CloudIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.cloudSync.title') || 'Cloud Sync',
      description: t('landing.features.cloudSync.description') || 'Access your book collection from anywhere with secure cloud synchronization.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.secure.title') || 'Secure & Private',
      description: t('landing.features.secure.description') || 'Your book collection data is private and secure with enterprise-grade encryption.'
    }
  ];

  const benefits = [
    t('landing.benefits.neverLose') || 'Never lose track of your books again',
    t('landing.benefits.trackValue') || 'Track the value of your collection',
    t('landing.benefits.planPurchases') || 'Plan your next book purchases',
    t('landing.benefits.shareCollection') || 'Share your collection with friends',
    t('landing.benefits.mobileFriendly') || 'Access from any device',
    t('landing.benefits.freeToUse') || 'Completely free to use'
  ];

  const howItWorks = [
    {
      step: 1,
      title: t('landing.howItWorks.signUp.title') || 'Sign Up',
      description: t('landing.howItWorks.signUp.description') || 'Create your free account in seconds',
      icon: <StarIcon sx={{ fontSize: 30, color: 'primary.main' }} />
    },
    {
      step: 2,
      title: t('landing.howItWorks.addBooks.title') || 'Add Books',
      description: t('landing.howItWorks.addBooks.description') || 'Add your books with detailed information',
      icon: <AddIcon sx={{ fontSize: 30, color: 'primary.main' }} />
    },
    {
      step: 3,
      title: t('landing.howItWorks.organize.title') || 'Organize',
      description: t('landing.howItWorks.organize.description') || 'Organize by genre, author, or any criteria',
      icon: <ViewListIcon sx={{ fontSize: 30, color: 'primary.main' }} />
    },
    {
      step: 4,
      title: t('landing.howItWorks.monitor.title') || 'Monitor',
      description: t('landing.howItWorks.monitor.description') || 'Monitor your collection growth and value',
      icon: <SpeedIcon sx={{ fontSize: 30, color: 'primary.main' }} />
    }
  ];

  const handleGetStarted = () => {
    if (currentUser) {
      navigate('/books');
    } else {
      navigate('/login');
    }
  };

  const handleSignUp = () => {
    if (currentUser) {
      navigate('/books');
    } else {
      navigate('/signup');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Language Switcher AppBar */}
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: 'flex-end' }}>
          <IconButton
            onClick={handleLanguageClick}
            sx={{ color: 'text.primary' }}
            title="Change Language"
          >
            <LanguageIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageClose}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                selected={currentLanguage === lang.code}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #8B7355 0%, #D2B48C 100%)',
        color: 'white',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  lineHeight: 1.2
                }}
              >
                {t('landing.title') || 'BookPort'}
              </Typography>
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 400,
                  opacity: 0.9,
                  mb: 3
                }}
              >
                {t('landing.subtitle') || 'Your Personal Book Collection Manager'}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.8,
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                {t('landing.description') || 'Organize, track, and manage your book collection with ease. From novels to textbooks, keep everything in one beautiful, organized place.'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                >
                  {currentUser ? (t('landing.goToCollection') || 'Go to Collection') : (t('landing.getStarted') || 'Get Started')}
                </Button>
                {!currentUser && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleSignUp}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    {t('landing.signUp') || 'Sign Up'}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Paper
                  elevation={8}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    color: 'text.primary',
                    maxWidth: 400,
                    width: '100%'
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
                    {t('landing.sampleCollection') || 'Sample Collection'}
                  </Typography>
                  <Grid container spacing={2}>
                    {exampleBooks.map((book, index) => (
                      <Grid item xs={12} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderRadius: 1, bgcolor: 'grey.50' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: 'primary.main', 
                              mr: 2,
                              width: 50,
                              height: 50
                            }}
                          >
                            <img 
                              src={book.imageUrl} 
                              alt={book.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {book.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {book.author} • {book.publisher} • {book.genre}
                            </Typography>
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            ฿{book.price.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            {t('landing.features.title') || 'Features'}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            {t('landing.features.subtitle') || 'Everything you need to manage your book collection'}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  textAlign: 'center',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              {t('landing.howItWorks.title') || 'How It Works'}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              {t('landing.howItWorks.subtitle') || 'Get started in just a few simple steps'}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {howItWorks.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box textAlign="center">
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    mb: 2,
                    position: 'relative'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {step.step}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              {t('landing.benefits.title') || 'Why Choose BookPort?'}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 4, lineHeight: 1.6 }}
            >
              {t('landing.benefits.subtitle') || 'Join thousands of book lovers who trust BookPort to manage their collections'}
            </Typography>
            <List>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckIcon sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={benefit}
                    primaryTypographyProps={{
                      variant: 'body1',
                      sx: { fontWeight: 500 }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                bgcolor: 'white'
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                {t('landing.readerTypes.title') || 'Perfect for All Readers'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                <Chip label="นิยาย" color="primary" />
                <Chip label="วรรณกรรม" color="primary" />
                <Chip label="หนังสือเด็ก" color="primary" />
                <Chip label="หนังสือเรียน" color="primary" />
                <Chip label="หนังสือวิชาการ" color="primary" />
                <Chip label="หนังสือการ์ตูน" color="primary" />
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('landing.readerTypes.description') || 'Whether you collect novels, textbooks, comics, or rare books, BookPort adapts to your collection style.'}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                startIcon={<PlayIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
              >
                {t('landing.readerTypes.startCollection') || 'Start Your Collection'}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #8B7355 0%, #D2B48C 100%)',
        color: 'white',
        py: { xs: 6, md: 8 }
      }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              {t('landing.cta.title') || 'Ready to Organize Your Books?'}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                mb: 4,
                lineHeight: 1.6
              }}
            >
              {t('landing.cta.subtitle') || 'Join thousands of book lovers who have already organized their collections with BookPort'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'grey.100'
                  }
                }}
              >
                {currentUser ? (t('landing.goToCollection') || 'Go to Collection') : (t('landing.getStarted') || 'Get Started')}
              </Button>
              {!currentUser && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleSignUp}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  {t('landing.cta.createAccount') || 'Create Account'}
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage; 